import {TFunction} from 'i18next';

import {Group, Subgroup} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.GROUPS.GROUP_INFO.ERRORS;

export interface FieldError {
    [key: string]: string | boolean;
}

export class GroupInfoService {
    private static isUniqByKey<T>(array: T[], item: T, key: string): boolean {
        const toLower = (str: string) => String(str).toLowerCase();
        return array.some((p) => p[key] && toLower(p[key]) === toLower(item[key]) && p !== item);
    }

    private static getUniqueFieldErrors<T>(array: T[], item: T, fields: { key: string, message: string, errorKey: string }[]): FieldError {
        if (!item || !Array.isArray(array)) {
            return {};
        }
        const errors: FieldError = {};
        fields.forEach((field) => {
            if (this.isUniqByKey(array, item, field.key)) {
                errors[field.errorKey] = field.message;
            }
        });

        return errors;
    }

    private static getEmptyFieldErrors<T>(item: T, fields: { key: string, message: string, errorKey: string }[]): FieldError {
        if (!item) {
            return {};
        }
        const errors: FieldError = {};
        fields.forEach((field) => {
            if (!item[field.key]) {
                errors[field.errorKey] = field.message;
            }
        });

        return errors;
    }

    static getGroupErrors(t: TFunction, groups: Group[], group: Group): FieldError {

        const uniqueFieldErrors = this.getUniqueFieldErrors(
            groups,
            group,
            [
                {key: 'name', errorKey: 'name', message: t(T_KEYS.NAME_UNIQUE)},
                {key: 'publicId', errorKey: 'publicId', message: t(T_KEYS.ID_UNIQUE)}
            ]
        );

        const emptyFieldErrors = this.getEmptyFieldErrors(
            group,
            [
                {key: 'name', errorKey: 'name', message: t(T_KEYS.NAME_EMPTY)},
                {key: 'publicId', errorKey: 'publicId', message: t(T_KEYS.ID_EMPTY)}
            ]
        );

        return {
            ...uniqueFieldErrors,
            ...emptyFieldErrors
        };
    }

    static getSubgroupError(t: TFunction, subgroups: Subgroup[], subgroup: Subgroup, key?: string): FieldError {

        const uniqueFieldErrors = this.getUniqueFieldErrors(
            subgroups,
            subgroup,
            [
                {key: 'name', errorKey: 'name', message: t(T_KEYS.NAME_UNIQUE)},
                {key: 'publicId', errorKey: 'publicId', message: t(T_KEYS.ID_UNIQUE)}
            ].filter((field) => !key || field.key === key)
        );

        const emptyFieldErrors = this.getEmptyFieldErrors(
            subgroup,
            [
                {key: 'name', errorKey: 'name', message: t(T_KEYS.NAME_EMPTY)},
                {key: 'publicId', errorKey: 'publicId', message: t(T_KEYS.ID_EMPTY)}
            ].filter((field) => !key || field.key === key)
        );

        return {
            ...uniqueFieldErrors,
            ...emptyFieldErrors
        };
    }
}
